import { useState, useEffect } from 'react';
import { FiArrowLeft, FiEye, FiEyeOff, FiHelpCircle, FiLock, FiLogIn, FiRefreshCw, FiShield, FiSmartphone, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const Login = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const { login, loginAdmin, completeAdminLogin, isLoading } = useAuth();

  const navigate = useNavigate();

  // Resend OTP countdown timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleRegularSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!userId || !password) {
      setError('Please enter both User ID and Password');
      return;
    }
    try {
      const userData = await login(userId, password);
      const role = userData?.role?.toLowerCase();
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'engineer') {
        navigate('/engineer/dashboard');
      } else {
        navigate('/user/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Invalid User ID or Password');
    }
  };

  const handleAdminOtpRequest = async (e) => {
    e.preventDefault();
    setError('');
    if (!userId) {
      setError('Please enter your User ID');
      return;
    }
    try {
      const result = await loginAdmin(userId);
      setMaskedPhone(result.maskedPhone || '');
      setOtpStep(true);
      setResendTimer(30); // Start 30s cooldown
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError('');
    try {
      const result = await loginAdmin(userId);
      setMaskedPhone(result.maskedPhone || '');
      setResendTimer(30); // 30 second cooldown
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    }
  };

  const handleAdminOtpVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp || otp.length < 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    try {
      const userData = await completeAdminLogin(otp);
      const role = userData?.role?.toLowerCase();
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    }
  };

  const toggleAdminMode = () => {
    setIsAdminMode(!isAdminMode);
    setOtpStep(false);
    setOtp('');
    setPassword('');
    setError('');
  };

  const goBackToUserId = () => {
    setOtpStep(false);
    setOtp('');
    setError('');
  };

  return (
    <div className="login-container">
      <header className="login-header">
        <div className="login-header-center">
          <h1>{isAdminMode ? 'Admin Login' : 'Login'}</h1>
          <p>{isAdminMode ? 'Secure OTP verification' : 'Access your support portal'}</p>
        </div>
      </header>

      <div className="login-content">
        <div className="login-branding">
          <h2 className="login-title">Nexstep Support Portal</h2>
          <p className="login-subtitle">
            {isAdminMode
              ? 'Admin secure login with OTP verification.'
              : 'Login to manage your service requests, complaints, and admin tasks.'}
          </p>
          <p className="login-hint">
            {isAdminMode
              ? 'Enter your admin User ID to receive OTP.'
              : 'Use User ID and password created by your admin.'}
          </p>
        </div>

        {isAdminMode ? (
          otpStep ? (
            <form onSubmit={handleAdminOtpVerify} className="login-form">
              {error && <div className="login-error">{error}</div>}

              <button type="button" className="login-back-btn" onClick={goBackToUserId}>
                <FiArrowLeft size={18} />
                <span>Back to User ID</span>
              </button>

              <div className="login-otp-card">
                <div className="otp-phone-icon">
                  <FiSmartphone size={40} />
                </div>
                <p className="otp-sent-text">OTP sent to WhatsApp</p>
                <strong className="otp-phone-number">{maskedPhone || 'your registered phone'}</strong>
                <p className="otp-hint">Enter the 6-digit code below</p>
              </div>

              <div className="login-input-container otp-input-container">
                <label htmlFor="login-otp" className="login-label">OTP Code</label>
                <div className="login-input-wrapper otp-wrapper">
                  <FiShield className="login-input-icon" />
                  <input
                    id="login-otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    className="otp-input"
                  />
                </div>
                <div className="otp-dots">
                  {[...Array(6)].map((_, i) => (
                    <span key={i} className={`otp-dot ${otp.length > i ? 'filled' : ''}`} />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="login-btn verify-btn"
                disabled={otp.length < 6 || isLoading}
              >
                {isLoading ? (
                  <span className="login-spinner" />
                ) : (
                  <>
                    <FiShield className="login-btn-icon" />
                    Verify OTP
                  </>
                )}
              </button>

              <button
                type="button"
                className="resend-otp-btn"
                onClick={handleResendOtp}
                disabled={resendTimer > 0 || isLoading}
              >
                <FiRefreshCw size={16} className={resendTimer > 0 ? '' : 'spin-icon'} />
                {resendTimer > 0 ? (
                  <span>Resend in {resendTimer}s</span>
                ) : (
                  <span>Resend OTP</span>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleAdminOtpRequest} className="login-form">
              {error && <div className="login-error">{error}</div>}

              <div className="login-input-container">
                <label htmlFor="admin-userId" className="login-label">Admin User ID</label>
                <div className="login-input-wrapper">
                  <FiUser className="login-input-icon" />
                  <input
                    id="admin-userId"
                    type="text"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter your admin User ID"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                className="login-btn admin-btn"
                disabled={!userId || isLoading}
              >
                {isLoading ? (
                  <span className="login-spinner" />
                ) : (
                  <>
                    <FiSmartphone className="login-btn-icon" />
                    Send OTP
                  </>
                )}
              </button>

              <button type="button" className="login-mode-toggle" onClick={toggleAdminMode}>
                <FiLock size={16} />
                <span>Back to regular login</span>
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleRegularSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="login-input-container">
              <label htmlFor="login-userId" className="login-label">User ID</label>
              <div className="login-input-wrapper">
                <FiUser className="login-input-icon" />
                <input
                  id="login-userId"
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter your user ID"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
            </div>

            <div className="login-input-container">
              <label htmlFor="login-password" className="login-label">Password</label>
              <div className="login-input-wrapper">
                <FiLock className="login-input-icon" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={22} /> : <FiEye size={22} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn"
              disabled={!userId || !password || isLoading}
            >
              {isLoading ? (
                <span className="login-spinner" />
              ) : (
                <>
                  <FiLogIn className="login-btn-icon" />
                  Sign In
                </>
              )}
            </button>

            <div className="login-options">
              <button type="button" className="login-mode-toggle" onClick={toggleAdminMode}>
                <FiShield size={16} />
                <span>Admin OTP Login</span>
              </button>
            </div>

            <button type="button" className="login-support-link">
              <FiHelpCircle size={16} />
              <span>Need help? Contact support</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
